import { useTranslation } from "react-i18next";
import { IconCheckCircle } from "../../../icons/IconCheckCircle/IconCheckCircle";
import { LeafMessageBox, LeafMessageBoxColor } from "../../../leaf/LeafMessageBox/LeafMessageBox";
import {
  getFiscalMonthNumber,
  getFullMonth,
  getPerformanceYearShort,
  getSelectedPerformanceYearData,
  numMonthsBetweenDates,
} from "../../../util/period";
import { BaseTile } from "../../BaseTile/BaseTile";
import { PeriodCircleLegendLoader } from "../../PeriodsProgressCircle/PeriodsCircleLegend/PeriodsCircleLegend";
import { PeriodsProgressCircle } from "../../PeriodsProgressCircle/PeriodsProgressCircle";
import "./AnnualVolumeRequirementTile.scss";
import defaultContent from "../../../../content/annualVolumeRequirementTile";
import LeafRectangleLoader from "../../../leaf/LeafLoadingSkeleton/LeafRectangleLoader/LeafRectangleLoader";
import { VolumeRequirementLoader } from "../VolumeRequirementTile/Loader/VolumeRequirementLoader";
import { formatKpiValue } from "../../../util/format";
import { useAppSelector } from "../../../../store";

const Loader = () => {
  return (
    <div className="AnnualVolumeRequirementTile">
      <BaseTile>
        <section className="AnnualVolumeRequirementTile__content">
          <div className="AnnualVolumeRequirementTile__leftSide AnnualVolumeRequirementTile__leftSide--loading">
            {/* Title bar */}
            <LeafRectangleLoader isLoading height="28px" width="90%" margin="0" />
            {/* Status message box - taller like tracking box */}
            <LeafRectangleLoader isLoading height="36.5px" width="85%" margin="0" />
            {/* Text lines - grouped closer together */}
            <div className="AnnualVolumeRequirementTile__loaderTextGroup">
              <LeafRectangleLoader isLoading height="20px" width="90%" margin="0" />
              <LeafRectangleLoader isLoading height="20px" width="90%" margin="0" />
            </div>
          </div>

          <aside className="AnnualVolumeRequirementTile__rightSide">
            <VolumeRequirementLoader border={false} showHeader={false} />
            <div className="AnnualVolumeRequirementTile__legendGroupLoader">
              <PeriodCircleLegendLoader />
            </div>
          </aside>
        </section>
      </BaseTile>
    </div>
  );
};

type volumeAmount = {
  actual: number;
  formatted: string;
};

type props = {
  isLoading: boolean;
  currentAmount: volumeAmount;
  targetAmount: volumeAmount;
  met: boolean;
  volumeType: "personal" | "group" | "ruby" | "annualGpv";
  exempt?: boolean;
  hideBorder?: boolean;
  disableTile?: boolean;
  customDescription?: string;
};

const getMessageKeyAndColor = (tracking: boolean, met: boolean, exempt: boolean) => {
  switch (true) {
    case exempt:
      return {
        key: "exempt",
        color: LeafMessageBoxColor.BLUE,
      };
    case met:
      return {
        key: "achieved",
        color: LeafMessageBoxColor.GREEN,
      };
    case tracking:
      return {
        key: "tracking",
        color: LeafMessageBoxColor.BLUE,
      };
    default:
      return {
        key: "notTracking",
        color: LeafMessageBoxColor.ORANGE,
      };
  }
};

export const AnnualVolumeRequirementTile = ({
  isLoading,
  currentAmount,
  targetAmount,
  met,
  volumeType,
  exempt,
  disableTile = false,
  customDescription,
}: props) => {
  const { t } = useTranslation(["annualVolumeRequirementTile"]);
  const { user } = useAppSelector((store) => store.boot);
  const { loginFields } = useAppSelector((store) => store.loginLocalStorage);
  const { selectedPeriod } = useAppSelector((store) => store.period);
  const currentPeriod = parseInt(selectedPeriod, 10);
  const annualVolumeType = t(`annual.${volumeType}`, defaultContent["annual"][volumeType] as string);
  const monthlyVolumeType = t(`monthly.${volumeType}`, defaultContent["monthly"][volumeType] as string);
  const monthlyVolumeNeeded = (targetAmount.actual / 12).toString();
  const monthlyVolumeNeededInt = Number(monthlyVolumeNeeded);

  const centralAsiaCountryCodes = ["KZ", "KG", "MN"];
  const isCentralAsiaMarket = centralAsiaCountryCodes.includes(user.isoCountryCode);
  const shouldHideLine = isCentralAsiaMarket && (volumeType === "personal" || volumeType === "ruby");

  const { selectedPYStartPeriod } = getSelectedPerformanceYearData(currentPeriod.toString());
  const currentMonthTarget =
    (targetAmount.actual / 12) * numMonthsBetweenDates(selectedPYStartPeriod, currentPeriod.toString());
  const monthNumber = getFiscalMonthNumber(selectedPeriod);

  const tracking = currentAmount.actual >= monthNumber * monthlyVolumeNeededInt - monthlyVolumeNeededInt;
  const { key: messageKey, color: messageColor } = getMessageKeyAndColor(tracking, met, exempt || false);
  let currentVolumeColor = tracking ? "green" : "orange";
  if (exempt) currentVolumeColor = "black";
  const mainClassName = `AnnualVolumeRequirementTile ${disableTile ? "disabledMode" : ""} ${
    exempt ? "AnnualVolumeRequirementTile--exempt" : ""
  }`;

  const chartTitle = () => {
    return (
      <>
        <h3 className={exempt ? `PeriodsCircleOverlay--disabled` : ""}>
          {t("pyChartTitle", defaultContent.pyChartTitle as string, {
            perfYear: getPerformanceYearShort(currentPeriod),
          })}
        </h3>
        <p className={exempt ? `PeriodsCircleOverlay--disabled` : ""}>
          ({getFullMonth(currentPeriod, loginFields.locale)})
        </p>
      </>
    );
  };

  const getAccessibleDescription = () => {
    if (exempt) {
      return String(
        t("accessibility.exempt", defaultContent.accessibility.exempt, {
          volumeType: annualVolumeType,
        }),
      );
    }

    const statusKey = met ? "achieved" : tracking ? "onTrack" : "belowTarget";
    const statusText = String(
      t(
        `accessibility.statusValues.${statusKey}`,
        defaultContent.accessibility.statusValues[statusKey as keyof typeof defaultContent.accessibility.statusValues],
      ),
    );

    return String(
      t("accessibility.status", defaultContent.accessibility.status, {
        volumeType: annualVolumeType,
        current: currentAmount.formatted,
        target: targetAmount.formatted,
        status: statusText,
      }),
    );
  };

  return isLoading ? (
    <Loader />
  ) : (
    <div
      className={mainClassName}
      role="region"
      aria-labelledby="volume-requirement-title"
      aria-describedby="volume-requirement-description"
    >
      <BaseTile>
        <section className="AnnualVolumeRequirementTile__content">
          <div className="AnnualVolumeRequirementTile__leftSide">
            <div id="volume-requirement-description" className="sr-only">
              {getAccessibleDescription()}
            </div>

            <div className="AnnualVolumeRequirementTile__tileHeader">
              <IconCheckCircle
                aria-label={String(t("accessibility.icon", met ? "Requirement achieved" : "Requirement not achieved"))}
                color={disableTile || exempt ? "gray" : met ? "green" : "gray"}
                size="md"
              />
              <h2
                id="volume-requirement-title"
                className={exempt ? "AnnualVolumeRequirementTile__title--exempt" : ""}
                aria-live="polite"
              >
                {exempt && <span className="sr-only">{String(t("accessibility.exemptPrefix", "Exempt: "))}</span>}
                {`${currentAmount.formatted}/${targetAmount.formatted} ${annualVolumeType}`}
              </h2>
            </div>

            <LeafMessageBox color={messageColor}>
              {t(`status.${messageKey}`, defaultContent["status"][messageKey] as string, {
                volumeType: annualVolumeType,
              })}
            </LeafMessageBox>
            <div className="AnnualVolumeRequirementTile__description">
              <section className="AnnualVolumeRequirementTile__monthlyStatus">
                <div className="AnnualVolumeRequirementTile__monthlyStatus--row">
                  <span>
                    {t("volumeYtd", defaultContent.volumeYtd as string, {
                      volumeType: monthlyVolumeType,
                    })}
                  </span>
                  <strong className={`AnnualVolumeRequirementTile__currentVolume--${currentVolumeColor}`}>
                    {currentAmount.formatted}
                  </strong>
                </div>
                {!shouldHideLine && (
                  <div className="AnnualVolumeRequirementTile__monthlyStatus--row-inline">
                    <span>
                      {t("neededForOnTrack", defaultContent.neededForOnTrack as string, {
                        volumeType: monthlyVolumeType,
                        currentMonth: getFullMonth(currentPeriod, loginFields.locale),
                      })}
                    </span>
                    <strong>
                      {formatKpiValue(volumeType, currentMonthTarget, user.isoCountryCode, loginFields.locale)}
                    </strong>
                  </div>
                )}
              </section>
              <span className={exempt ? "AnnualVolumeRequirementTile__description--exempt" : ""}>
                {customDescription || t("description", defaultContent.description as string)}
              </span>
            </div>
          </div>

          <aside className="AnnualVolumeRequirementTile__rightSide">
            <PeriodsProgressCircle
              period={currentPeriod.toString()}
              targetAmount={targetAmount}
              currentAmount={currentAmount}
              tracking={tracking}
              disabled={exempt || disableTile}
              showLegend={true}
              chartTitle={chartTitle()}
            />
          </aside>
        </section>
        {exempt && (
          <div className="sr-only" aria-live="polite">
            {String(
              t("accessibility.exemptAnnouncement", defaultContent.accessibility.exemptAnnouncement, {
                volumeType: annualVolumeType,
              }),
            )}
          </div>
        )}
      </BaseTile>
    </div>
  );
};
